import lowerFirst from "lodash-es/lowerFirst";

import { BurstVerdict, getBurstVerdict } from "shared/Usage/utils";
import { formatMinutes } from "views/Account/Settings/Usage/utils";

import { WorkerUsageBurstBudget, WorkerUsageData, WorkerUsageWithContract } from "../types";
import {
  getReferencePercentileTooltip,
  getWorkerUsagePeak,
  hasWorkerUsageContract,
  isWorkerUsageAboveContract,
  isWorkerUsagePastCycle,
} from "../utils";
import {
  ABOVE_CONTRACT_TOOLTIP,
  BURST_BUDGET_TOOLTIP,
  MULTI_ACCOUNT_CONTRACT_BURST_TOOLTIP,
  MULTI_ACCOUNT_CONTRACT_CAPTION,
  MULTI_ACCOUNT_CONTRACT_PERCENTILE_CAPTION,
  MULTI_ACCOUNT_CONTRACT_VALUE,
  PEAK_TOOLTIP,
  ZERO_BURST_BUDGET_TOOLTIP,
} from "./constants";
import { WorkerUsageMetricCard, WorkerUsageMetricCardSeverity } from "./types";

type BurstMeasuredVerdict = Exclude<BurstVerdict, "no-allowance">;

const getPercentileCaption = (caption: string, usage: WorkerUsageData) =>
  isWorkerUsagePastCycle(usage) ? `Final · ${lowerFirst(caption)}` : caption;

const getBurstCard = (burst: WorkerUsageBurstBudget): WorkerUsageMetricCard => {
  const { budgetMinutes, usedMinutes, remainingMinutes } = burst;

  const verdict = getBurstVerdict(burst);

  if (verdict === "no-allowance") {
    return {
      label: "Burst budget (min)",
      value: "0",
      caption: "Not applicable",
      infoTooltip: ZERO_BURST_BUDGET_TOOLTIP,
    };
  }

  const usedCaption = `${formatMinutes(usedMinutes)} of ${formatMinutes(budgetMinutes)} min used`;

  const BURST_CAPTION: Record<BurstMeasuredVerdict, string> = {
    "on-track": usedCaption,
    approaching: usedCaption,
    over: "Over limit",
  };

  const BURST_SEVERITY: Record<BurstMeasuredVerdict, WorkerUsageMetricCardSeverity | undefined> = {
    "on-track": undefined,
    approaching: "warning",
    over: "danger",
  };

  const BURST_SEVERITY_TOOLTIP: Record<BurstMeasuredVerdict, string | undefined> = {
    "on-track": undefined,
    approaching: `You've used ${formatMinutes(usedMinutes)} of your ${formatMinutes(
      budgetMinutes
    )} min burst allowance — running low for the rest of the cycle.`,
    over: `Over your burst allowance — ${formatMinutes(
      usedMinutes - budgetMinutes
    )} min above the ${formatMinutes(budgetMinutes)} min allowed. This may incur overage charges.`,
  };

  return {
    label: "Burst budget (min)",
    value: formatMinutes(remainingMinutes),
    caption: BURST_CAPTION[verdict],
    severity: BURST_SEVERITY[verdict],
    severityTooltip: BURST_SEVERITY_TOOLTIP[verdict],
    infoTooltip: BURST_BUDGET_TOOLTIP,
  };
};

// Nothing to judge usage against, so the tiles drop to plain observations.
const getTrendOnlyCards = (usage: WorkerUsageData): WorkerUsageMetricCard[] => {
  const { fallbackPercentile, fallbackPercentileValue } = usage;

  const cards: WorkerUsageMetricCard[] = [
    {
      label: "Peak workers",
      value: `${getWorkerUsagePeak(usage)}`,
      caption: "Highest day this cycle",
      infoTooltip: PEAK_TOOLTIP,
    },
  ];

  if (fallbackPercentileValue !== undefined) {
    cards.push({
      label: `P${fallbackPercentile} workers`,
      value: `${fallbackPercentileValue}`,
      caption: isWorkerUsagePastCycle(usage) ? "Final" : "To date",
      infoTooltip: getReferencePercentileTooltip(fallbackPercentile),
    });
  }

  return cards;
};

const getContractedCards = (usage: WorkerUsageWithContract): WorkerUsageMetricCard[] => {
  const { contractedWorkers, contractedPercentile, percentileToDateValue } = usage;

  const isAboveContract = isWorkerUsageAboveContract(usage);

  const cards: WorkerUsageMetricCard[] = [
    {
      label: "Contracted workers",
      value: `${contractedWorkers}`,
      caption: `P${contractedPercentile} limit`,
    },
    {
      label: `P${contractedPercentile} workers`,
      value: `${percentileToDateValue}`,
      caption: getPercentileCaption(isAboveContract ? "Above contract" : "Within contract", usage),
      // Expected under a percentile model, so it warns but never turns red — only burst can.
      severity: isAboveContract ? "warning" : undefined,
      severityTooltip: isAboveContract ? ABOVE_CONTRACT_TOOLTIP : undefined,
      infoTooltip: `This is your P${contractedPercentile} for the cycle so far — where the percentile line ends on the chart. Your bill is calculated over the full cycle, so this number can still change before the cycle ends.`,
    },
  ];

  if (usage.burst) {
    cards.push(getBurstCard(usage.burst));
  }

  return cards;
};

const getMultiAccountContractCards = (usage: WorkerUsageWithContract): WorkerUsageMetricCard[] => {
  const { contractedPercentile, percentileToDateValue } = usage;

  return [
    {
      label: "Contracted workers",
      value: MULTI_ACCOUNT_CONTRACT_VALUE,
      caption: MULTI_ACCOUNT_CONTRACT_CAPTION,
    },
    {
      label: `P${contractedPercentile} workers`,
      value: `${percentileToDateValue}`,
      caption: getPercentileCaption(MULTI_ACCOUNT_CONTRACT_PERCENTILE_CAPTION, usage),
      infoTooltip: getReferencePercentileTooltip(contractedPercentile),
    },
    {
      label: "Burst budget (min)",
      value: MULTI_ACCOUNT_CONTRACT_VALUE,
      caption: MULTI_ACCOUNT_CONTRACT_CAPTION,
      infoTooltip: MULTI_ACCOUNT_CONTRACT_BURST_TOOLTIP,
    },
  ];
};

export const getWorkerUsageCards = (usage: WorkerUsageData): WorkerUsageMetricCard[] => {
  if (!hasWorkerUsageContract(usage)) {
    return getTrendOnlyCards(usage);
  }

  return usage.multiAccountContract
    ? getMultiAccountContractCards(usage)
    : getContractedCards(usage);
};
